/**
 * /livret-plateau/print — Version imprimable / PDF du Livret Plateau
 *
 * Version imprimable premium du livret plateau A1/A2 — même modèle que
 * `/livret-circulation/print`. Auto-lance window.print() au chargement.
 * Tout le contenu est déployé sur un seul flow avec sauts de page entre
 * chapitres. Case vierges pour cocher à la main après impression.
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Printer, ArrowLeft, Square } from 'lucide-react';

const CHAPTERS_DATA = [
  {
    n: '01',
    title: 'Bienvenue sur le plateau',
    subtitle: 'Construire de vrais automatismes',
    intro: "Le plateau n'est pas juste une succession de cônes à mémoriser. C'est là que tu construis ton équilibre, ta précision, ton regard et la maîtrise de tes commandes. Chaque passage te donne une information : cherche à comprendre ce qui change sur la moto, puis recommence.",
    section1Title: '🎯 4 repères mentaux à garder en tête',
    items: [
      { label: 'REGARD', desc: 'Loin, mobile, vers la sortie' },
      { label: 'DOUCEUR', desc: "Pas d'à-coups inutiles" },
      { label: 'ÉQUILIBRE', desc: 'Corps souple, moto guidée' },
      { label: 'RÉGULARITÉ', desc: 'Répéter pour automatiser' },
    ],
  },
  {
    n: '02',
    title: 'Moto · Équipement · Commandes',
    subtitle: 'Bien équipé, bien installé, prêt à travailler',
    intro: "5 éléments EPI obligatoires ou recommandés, 4 commandes principales à connaître, 5 points d'appui pour ne faire qu'un avec la moto.",
    section1Title: '🦺 Ton équipement (5 éléments)',
    items: [
      { label: 'CASQUE', desc: 'Homologué, attaché et ajusté' },
      { label: 'BLOUSON', desc: 'Protection épaules/coudes/dos' },
      { label: 'GANTS', desc: 'Homologués et adaptés' },
      { label: 'PANTALON', desc: 'Protection adaptée à la pratique' },
      { label: 'CHAUSSURES', desc: 'Montantes, malléoles protégées' },
    ],
    section2Title: '🎮 4 commandes à identifier sans hésiter',
    items2: [
      { label: 'MAIN GAUCHE', desc: 'Embrayage' },
      { label: 'PIED GAUCHE', desc: 'Sélecteur de vitesses' },
      { label: 'MAIN DROITE', desc: 'Accélérateur + frein avant' },
      { label: 'PIED DROIT', desc: 'Frein arrière' },
    ],
  },
  {
    n: '03',
    title: 'Ton parcours plateau',
    subtitle: 'À toi de le dessiner',
    intro: "Dessine le parcours que tu vas apprendre : place les cônes, le départ, l'arrivée et indique le sens de circulation. Le refaire de mémoire est déjà un exercice de préparation. Quand tu sais expliquer le parcours, tu commences déjà à mieux le piloter.",
    section1Title: '✅ Je sais le refaire de mémoire',
    items: [
      { label: '☐ OUI', desc: 'Je peux tracer le parcours sans regarder' },
      { label: '☐ PRESQUE', desc: "J'y arrive avec de l'aide" },
      { label: '☐ À REVOIR', desc: "Je ne visualise pas encore l'ensemble" },
    ],
  },
  {
    n: '04',
    title: 'Manœuvres 1 à 3',
    subtitle: 'Manipulation · allure lente · freinage',
    intro: "Les 3 premières manœuvres officielles de l'épreuve plateau A2 selon l'arrêté du 20 avril 2012 modifié.",
    items: [
      { label: 'M1 — Sans l\'aide du moteur', desc: 'Maîtriser le poids · déplacer et rebéquiller proprement' },
      { label: 'M2 — Allure réduite', desc: 'Regard mobile et loin · patinage stable + frein arrière' },
      { label: "M3 — Freinage d'urgence", desc: 'Moto droite et regard loin · freinage progressif et précis' },
    ],
  },
  {
    n: '05',
    title: 'Manœuvres 4 à 6',
    subtitle: 'Passager · slalom · évitement',
    intro: "Les 3 dernières manœuvres officielles de l'épreuve plateau A2 — les plus techniques.",
    items: [
      { label: 'M4 — Allure réduite avec passager', desc: 'Anticiper le changement d\'équilibre · progressif' },
      { label: 'M5 — Slalom', desc: 'Trajectoire fluide · bras souples, regard vers la sortie' },
      { label: 'M6 — Évitement', desc: 'Déclencher franchement · redresser puis stabiliser' },
    ],
  },
  {
    n: '06',
    title: 'Ma progression',
    subtitle: 'Une lecture simple de ce qui est acquis',
    intro: "Coche pour chaque compétence son état : Découvert · En cours · Acquis. Reprends régulièrement cette page avec ton moniteur pour valider tes progrès.",
    items: [
      { label: 'Regard', desc: 'Découvert · En cours · Acquis' },
      { label: 'Position de conduite', desc: 'Découvert · En cours · Acquis' },
      { label: 'Manipulation à l\'arrêt', desc: 'Découvert · En cours · Acquis' },
      { label: 'Commandes moto', desc: 'Découvert · En cours · Acquis' },
      { label: 'Point de patinage', desc: 'Découvert · En cours · Acquis' },
      { label: 'Allure lente', desc: 'Découvert · En cours · Acquis' },
      { label: 'Freinage d\'urgence', desc: 'Découvert · En cours · Acquis' },
      { label: 'Passager', desc: 'Découvert · En cours · Acquis' },
      { label: 'Slalom', desc: 'Découvert · En cours · Acquis' },
      { label: 'Évitement', desc: 'Découvert · En cours · Acquis' },
      { label: 'Enchaînement complet', desc: 'Découvert · En cours · Acquis' },
      { label: 'Préparation examen', desc: 'Découvert · En cours · Acquis' },
    ],
  },
  {
    n: '07',
    title: "Prêt pour l'examen ?",
    subtitle: 'Check-list finale plateau',
    intro: "L'épreuve plateau dure ~15 minutes. Chaque erreur éliminatoire (chute, pied posé sur ligne, cône touché, chronomètre dépassé) t'oblige à recommencer. La confiance vient de la préparation.",
    items: [
      { label: 'Je connais l\'ordre des 6 manœuvres', desc: 'M1 → M6, dans le bon sens du parcours' },
      { label: 'Je garde mon regard loin et mobile', desc: "Pas de fixation sur le pneu ou le cône d'avant" },
      { label: 'Je maîtrise mes commandes sans précipitation', desc: 'Embrayage, gaz, freins dosés' },
      { label: 'Je reste concentré sur une zone à la fois', desc: 'Une manœuvre, un objectif' },
      { label: 'Je respire et je repars après une erreur', desc: 'Ne pas cumuler la tension' },
      { label: 'Mon moniteur me valide GO !', desc: 'La certitude vaut mieux que le doute' },
      { label: 'EPI complet vérifié le jour J', desc: 'Casque, gants, blouson, pantalon, chaussures' },
    ],
  },
];

function CheckLine({ item }) {
  return (
    <div className="print-check-line">
      <Square className="print-check-icon" strokeWidth={1.5} />
      <div>
        <div className="print-check-label">{item.label}</div>
        {item.desc && <div className="print-check-desc">{item.desc}</div>}
      </div>
    </div>
  );
}

function ChapterPrint({ ch }) {
  return (
    <section className="print-chapter" data-testid={`print-chapter-${ch.n}`}>
      <div className="print-chapter-header">
        <div className="print-chapter-num">Chapitre {ch.n}</div>
        <h2 className="print-chapter-title">{ch.title}</h2>
        {ch.subtitle && <div className="print-chapter-subtitle">{ch.subtitle}</div>}
      </div>

      {ch.intro && (
        <div className="print-chapter-intro">
          <div className="print-chapter-intro-label">À COMPRENDRE</div>
          <p>{ch.intro}</p>
        </div>
      )}

      {ch.section1Title && <div className="print-section-title">{ch.section1Title}</div>}
      <div className="print-items">
        {ch.items.map((item, i) => (
          <CheckLine key={i} item={item} />
        ))}
      </div>

      {ch.section2Title && (
        <>
          <div className="print-section-title">{ch.section2Title}</div>
          <div className="print-items">
            {ch.items2.map((item, i) => (
              <CheckLine key={i} item={item} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export default function LivretPlateauPrint() {
  useEffect(() => {
    document.title = 'Livret Plateau A2 · Version imprimable · CODE 2 ROUES';
    const t = setTimeout(() => { try { window.print(); } catch (_) { /* ignore */ } }, 800);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="print-livret" data-testid="livret-plateau-print">
      <style>{`
        @page { size: A4; margin: 18mm 15mm; }
        .print-livret {
          background: white; color: #111;
          font-family: 'Figtree', system-ui, -apple-system, sans-serif;
          max-width: 210mm; margin: 0 auto; padding: 24px; min-height: 100vh;
        }
        .print-toolbar {
          position: sticky; top: 0; background: white;
          border-bottom: 2px solid #dc2626;
          padding: 12px 16px; display: flex; gap: 12px;
          align-items: center; justify-content: space-between;
          z-index: 10; margin: -24px -24px 24px -24px;
        }
        .print-toolbar-actions { display: flex; gap: 10px; }
        .print-toolbar-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; border-radius: 8px; font-weight: 700; font-size: 13px;
          border: 2px solid #dc2626; color: #dc2626; background: white;
          cursor: pointer; text-decoration: none;
        }
        .print-toolbar-btn-primary { background: #dc2626; color: white; }

        .print-cover {
          text-align: center; padding: 40px 20px 60px;
          border-bottom: 4px solid #dc2626; margin-bottom: 32px;
        }
        .print-cover-kicker {
          font-size: 10px; font-weight: 900; letter-spacing: 3px;
          color: #dc2626; margin-bottom: 12px; text-transform: uppercase;
        }
        .print-cover-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 42px; font-weight: 900; line-height: 1.05; margin: 0 0 10px;
        }
        .print-cover-title span { color: #dc2626; }
        .print-cover-baseline {
          font-size: 13px; font-weight: 900; letter-spacing: 3px;
          color: #555; margin-top: 14px;
        }
        .print-cover-info {
          margin-top: 20px; padding: 14px 20px;
          background: #fef2f2; border: 1px solid #dc2626;
          border-radius: 12px; font-size: 12px; display: inline-block;
        }

        .print-profil {
          display: grid; grid-template-columns: 1fr 1fr 1fr;
          gap: 16px; margin: 24px 0 40px; padding: 20px;
          border: 2px dashed #999; border-radius: 12px;
        }
        .print-profil-label {
          font-size: 9px; font-weight: 900; letter-spacing: 2px;
          text-transform: uppercase; color: #666; margin-bottom: 6px;
        }
        .print-profil-line { border-bottom: 1px solid #666; height: 26px; }

        .print-chapter { page-break-before: always; margin-top: 20px; }
        .print-chapter:first-of-type { page-break-before: auto; }
        .print-chapter-header {
          border-bottom: 3px solid #dc2626; padding-bottom: 10px; margin-bottom: 20px;
        }
        .print-chapter-num {
          font-size: 10px; font-weight: 900; letter-spacing: 3px;
          color: #dc2626; text-transform: uppercase; margin-bottom: 4px;
        }
        .print-chapter-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 28px; font-weight: 900; line-height: 1.1; margin: 0 0 4px;
        }
        .print-chapter-subtitle { font-style: italic; color: #666; font-size: 13px; }

        .print-chapter-intro {
          background: #fef2f2; border-left: 4px solid #dc2626;
          padding: 12px 16px; margin: 16px 0 20px; border-radius: 0 8px 8px 0;
        }
        .print-chapter-intro-label {
          font-size: 9px; font-weight: 900; letter-spacing: 2px;
          color: #dc2626; text-transform: uppercase; margin-bottom: 4px;
        }
        .print-chapter-intro p { margin: 0; font-size: 12px; line-height: 1.6; }

        .print-section-title {
          font-size: 12px; font-weight: 900; text-transform: uppercase;
          letter-spacing: 1px; color: #333; margin: 20px 0 10px;
          border-bottom: 1px solid #ddd; padding-bottom: 4px;
        }

        .print-items { display: flex; flex-direction: column; gap: 8px; }
        .print-check-line {
          display: flex; gap: 10px; padding: 8px 10px;
          border: 1px solid #e5e5e5; border-radius: 6px; break-inside: avoid;
        }
        .print-check-icon {
          width: 18px; height: 18px; flex-shrink: 0; margin-top: 2px; color: #666;
        }
        .print-check-label { font-size: 12px; font-weight: 700; margin-bottom: 2px; }
        .print-check-desc { font-size: 10.5px; color: #666; line-height: 1.4; }

        .print-footer {
          page-break-before: always; text-align: center;
          padding-top: 60px; font-size: 11px; color: #666;
        }
        .print-footer-badge {
          display: inline-block; padding: 8px 20px;
          border: 2px solid #dc2626; border-radius: 20px;
          color: #dc2626; font-weight: 900; letter-spacing: 2px;
          text-transform: uppercase; font-size: 11px; margin-bottom: 20px;
        }

        @media print {
          .print-toolbar { display: none !important; }
          .print-livret { padding: 0; box-shadow: none; }
          body { background: white; }
        }
      `}</style>

      <div className="print-toolbar">
        <Link to="/livret-plateau" className="print-toolbar-btn">
          <ArrowLeft size={14} /> Retour au livret
        </Link>
        <div className="print-toolbar-actions">
          <button
            onClick={() => window.print()}
            className="print-toolbar-btn print-toolbar-btn-primary"
            data-testid="btn-print"
          >
            <Printer size={14} /> Imprimer / Enregistrer en PDF
          </button>
        </div>
      </div>

      <div className="print-cover">
        <div className="print-cover-kicker">CODE2ROUES.FR · VERSION IMPRIMABLE</div>
        <h1 className="print-cover-title">
          Livret élève<br />
          <span>Plateau A1/A2</span>
        </h1>
        <div className="print-cover-baseline">PRENDRE EN MAIN · MAÎTRISER · PROGRESSER</div>
        <div className="print-cover-info">
          <strong>7 chapitres pédagogiques</strong> · Épreuve plateau A2 (~15 min, 6 manœuvres officielles)
        </div>
      </div>

      <div className="print-profil">
        <div className="print-profil-field">
          <div className="print-profil-label">Élève</div>
          <div className="print-profil-line" />
        </div>
        <div className="print-profil-field">
          <div className="print-profil-label">Moniteur / auto-école</div>
          <div className="print-profil-line" />
        </div>
        <div className="print-profil-field">
          <div className="print-profil-label">Début formation</div>
          <div className="print-profil-line" />
        </div>
      </div>

      <div style={{ marginBottom: 40 }}>
        <div className="print-section-title">Sommaire</div>
        <ol style={{ paddingLeft: 20, fontSize: 12, lineHeight: 1.8 }}>
          {CHAPTERS_DATA.map((ch) => (
            <li key={ch.n}>
              <strong>Chapitre {ch.n}</strong> — {ch.title}
            </li>
          ))}
        </ol>
      </div>

      {CHAPTERS_DATA.map((ch) => (
        <ChapterPrint key={ch.n} ch={ch} />
      ))}

      <div className="print-footer">
        <div className="print-footer-badge">Tu es prêt(e)</div>
        <div>Fais confiance à ton moniteur, à ton apprentissage, et à tes automatismes construits pendant des dizaines d'heures.</div>
        <div style={{ marginTop: 12, fontSize: 10, color: '#999' }}>
          © Bruno LLORENS · ECSR diplômé d'État · CODE 2 ROUES · 2026<br />
          Support pédagogique officiel · code2roues.fr
        </div>
      </div>
    </div>
  );
}
